import { QdzElement, adoptSectionStyles, define, formatMoney, parseHTML, quickBuyStyle } from '@qdz/element';
import { addItems, openCart } from '@qdz/cart';

export class QdzQuickAdd extends QdzElement {
  #controller = null;
  #opener = null;
  #keep = null;

  get dialog() {
    return this.closest('qdz-dialog');
  }

  get content() {
    return this.querySelector('[data-qdz-quick-add-content]');
  }

  get rows() {
    return [...this.querySelectorAll('.qdz-quick-add__row[data-variant-id]')];
  }

  mount() {
    this.listen(document, 'click', (event) => {
      const trigger = event.target.closest('[data-qdz-quick-add]');
      if (!trigger) return;
      const style = quickBuyStyle(trigger);
      if (style === 'popup' && document.querySelector('qdz-quick-view:defined')) return;
      event.preventDefault();
      if (style === 'page') {
        window.location.assign(trigger.dataset.qdzQuickAdd);
        return;
      }
      this.#opener = trigger;
      this.load(trigger.dataset.qdzQuickAdd);
    });
    this.listen(this, 'change', (event) => {
      if (event.target.matches('[data-option-value-id]')) this.#reload();
    });
    this.listen(this, 'qdz:quantity:change', () => this.#sync());
    this.listen(this, 'click', (event) => {
      const pick = event.target.closest('[data-ref="pick"]');
      if (pick) this.#pick(pick.closest('.qdz-quick-add__row'));
      if (event.target.closest('[data-ref="add"]')) this.#add(this.items);
    });
    this.listen(this, 'qdz:fit:select', (event) => {
      const row = this.rows.find((element) => element.dataset.size.toLowerCase() === event.detail.size.toLowerCase());
      if (!row) return;
      event.preventDefault();
      for (const fit of this.querySelectorAll('[data-ref="fit"]')) fit.hidden = !row.contains(fit);
      if (this.content.hasAttribute('data-instant')) this.#add([{ id: Number(row.dataset.variantId), quantity: 1 }]);
      else if (this.#quantity(row) === 0) this.#setQuantity(row, 1);
    });
  }

  unmount() {
    this.#controller?.abort();
  }

  async load(productUrl, optionValues) {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', this.getAttribute('section-id'));
    if (optionValues) url.searchParams.set('option_values', optionValues);
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = parseHTML(await response.text());
      const fresh = doc.querySelector('[data-qdz-quick-add-content]');
      if (!fresh) throw new Error('missing');
      adoptSectionStyles(doc);
      const focusId = optionValues ? document.activeElement?.id : null;
      this.replaceChildren(fresh);
      if (!this.dialog?.isOpen) this.dialog?.open(this.#opener);
      if (focusId) document.getElementById(focusId)?.focus();
      this.#restore();
      this.#sync();
    } catch (error) {
      if (error.name !== 'AbortError') window.location.assign(productUrl);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  get items() {
    return this.rows
      .map((row) => ({ id: Number(row.dataset.variantId), quantity: this.#quantity(row) }))
      .filter((item) => item.quantity > 0);
  }

  #quantity(row) {
    return Number(row.querySelector('qdz-quantity-input input')?.value || 0);
  }

  #setQuantity(row, value) {
    const stepper = row.querySelector('qdz-quantity-input');
    if (stepper?.set) stepper.set(value);
  }

  #pick(row) {
    if (!row || row.querySelector('[data-ref="pick"]').disabled) return;
    if (this.content.hasAttribute('data-instant')) {
      this.#add([{ id: Number(row.dataset.variantId), quantity: 1 }]);
      return;
    }
    this.#setQuantity(row, this.#quantity(row) > 0 ? 0 : 1);
  }

  #reload() {
    this.#keep = Object.fromEntries(this.rows.map((row) => [row.dataset.size, this.#quantity(row)]).filter(([, quantity]) => quantity > 0));
    const ids = [...this.querySelectorAll('[data-option-value-id]')]
      .filter((element) => element.checked || element.hasAttribute('data-selected'))
      .map((element) => element.dataset.optionValueId);
    this.load(this.content.dataset.productUrl, ids.join(','));
  }

  #restore() {
    if (!this.#keep) return;
    for (const row of this.rows) {
      if (this.#keep[row.dataset.size]) this.#setQuantity(row, this.#keep[row.dataset.size]);
    }
    this.#keep = null;
  }

  #sync() {
    let count = 0;
    let total = 0;
    for (const row of this.rows) {
      const quantity = this.#quantity(row);
      row.classList.toggle('is-selected', quantity > 0);
      row.querySelector('[data-ref="pick"]').setAttribute('aria-pressed', String(quantity > 0));
      count += quantity;
      total += quantity * Number(row.dataset.price || 0);
    }
    const button = this.querySelector('[data-ref="add"]');
    if (button) {
      button.disabled = count === 0;
      if (count === 0) button.textContent = button.dataset.labelEmpty;
      else if (button.dataset.labelMode === 'text') button.textContent = button.dataset.label;
      else {
        const template = count === 1 ? button.dataset.labelOne : button.dataset.labelOther;
        button.textContent = template.replace('[count]', count).replace('[total]', formatMoney(total, button.dataset.moneyFormat || undefined));
      }
    }
    this.#hint(count, total);
  }

  #hint(picked, pickedTotal) {
    const hint = this.querySelector('[data-ref="hint"]');
    if (!hint) return;
    const units = Number(hint.dataset.count) + picked;
    const tiers = hint.dataset.tiers.split('|').filter(Boolean).map((tier) => {
      const [quantity, text] = tier.split('~');
      return { quantity: Number(quantity), text };
    });
    let offer = '';
    if (tiers.length) {
      const reached = tiers.filter((tier) => units >= tier.quantity).at(-1);
      const next = tiers.find((tier) => units < tier.quantity);
      if (!next) offer = hint.dataset.top.replace('[offer]', reached.text);
      else if (!reached) offer = (next.quantity - units === 1 ? hint.dataset.firstOne : hint.dataset.firstOther).replace('[count]', next.quantity - units).replace('[offer]', next.text);
      else offer = hint.dataset.next.replace('[count]', next.quantity - units).replace('[offer]', reached.text).replace('[next]', next.text);
    }
    hint.querySelector('[data-ref="offer"]').textContent = offer;
    const threshold = Number(hint.dataset.threshold);
    let shipping = '';
    if (threshold > 0) {
      const remaining = threshold - Number(hint.dataset.total) - pickedTotal;
      shipping = remaining > 0 ? hint.dataset.shipRemaining.replace('[amount]', formatMoney(remaining, hint.dataset.moneyFormat || undefined)) : hint.dataset.shipUnlocked;
    }
    hint.querySelector('[data-ref="shipping"]').textContent = shipping;
  }

  async #add(items) {
    const button = this.querySelector('[data-ref="add"]');
    const status = this.querySelector('[data-ref="status"]');
    if (!items.length) return;
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    try {
      await addItems(items);
      await this.dialog?.close();
      openCart(this.#opener);
    } catch (error) {
      if (status) status.textContent = error.message;
      if (button) button.disabled = false;
    } finally {
      button?.removeAttribute('aria-busy');
      this.removeAttribute('aria-busy');
    }
  }
}

define('qdz-quick-add', QdzQuickAdd);
